import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
} from "@mui/material";
import { useDispatch } from "react-redux";
import {
  createWorkflow,
  deleteWorkflowById,
  duplicateWorkflow,
  getWorkflowById,
  getWorkflowList,
  renameWorkflow,
  updateWorkflow,
} from "../../../../thunks/workflow";
import { showSnackbar } from "../../../../slice/snackbarSlice";
import {
  NOTIFICATION_MESSAGE,
  VALIDATION_MSG,
} from "../../../../utils/constant";
import { workflowValidation } from "./workflowvalidation";

const CreateUpdateWorkflowDialog = ({
  open,
  updateData,
  handleClose,
  isOpenCreateWorkflowDialog,
  getWorkflowList = () => {},
}) => {
  const dispatch = useDispatch();
  const [isLoader, setLoader] = useState(false);
  const [isError, setError] = useState(false);
  const isUpdateOperation = Boolean(updateData);

  const [createWorkflowData, setWorkflowData] = useState({
    name: "",
    description: "",
  });

  const handleChange = (key, value) => {
    setWorkflowData((prev) => ({ ...prev, [key]: value }));
  };

  // Method to create and update workflow data
  const handleClickCreateWorkflow = async () => {
    const isValidForm = workflowValidation?.(createWorkflowData);
    if (!isValidForm) {
      setError(true);
      return;
    }
    setError(false);
    try {
      setLoader(true);
      const payload = {
        workFlowStatusId:
          Number(createWorkflowData?.workFlowStatusId) || undefined,
        instructionTypeId:
          Number(isOpenCreateWorkflowDialog?.instructionId) ||
          updateData?.instructionId,
        name: createWorkflowData?.name || "",
        description: createWorkflowData?.description || "",
        // Only include ID for update operations
        ...(isUpdateOperation && { id: Number(updateData?.workflowId) }),
      };

      // Execute the appropriate action
      const response = isUpdateOperation
        ? updateData?.operationType === "rename"
          ? await dispatch(renameWorkflow(payload)).unwrap() // rename
          : updateData?.operationType === "duplicate" ||
            updateData?.operationType === "edit"
          ? await dispatch(duplicateWorkflow(payload)).unwrap() // duplicate
          : await dispatch(deleteWorkflowById(payload)).unwrap() // delete
        : await dispatch(createWorkflow(payload)).unwrap(); // create

      if (response) {
        dispatch(
          showSnackbar({
            show: true,
            message:
              isUpdateOperation && updateData?.operationType === "delete"
                ? NOTIFICATION_MESSAGE.DELETE_WORKFLOW_SUCCESS
                : NOTIFICATION_MESSAGE?.CREATE_WORKFLOW_SUCCESS,
            severity: "success",
          })
        );
        await getWorkflowList();
        handleClose();
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          show: true,
          message: `${
            error?.message || NOTIFICATION_MESSAGE.SOMETHING_WENT_WRONG
          }`,
          severity: "error",
        })
      );
    } finally {
      setLoader(false);
    }
  };

  // Method to get workflow by id
  const getWorkflowDataByID = async () => {
    try {
      setLoader(true);
      const response = await dispatch(
        getWorkflowById(updateData?.workflowId)
      ).unwrap();
      if (response) {
        setWorkflowData({
          name:
            updateData.operationType === "duplicate"
              ? `${response?.name}(copy)`
              : updateData?.operationType === "edit"
              ? `${response?.name}(edited)`
              : response?.name,
          description: response?.description,
          workFlowStatusId: response?.workFlowStatusId,
        });
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          show: true,
          message: `${
            error?.message || NOTIFICATION_MESSAGE.SOMETHING_WENT_WRONG
          }`,
          severity: "error",
        })
      );
      setWorkflowData({ name: "", description: "" });
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (updateData?.workflowId) {
      getWorkflowDataByID();
    }
  }, [updateData?.workflowId]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{ position: "absolute", right: 8, top: 8 }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle
        className="serif_display_regular_22 green"
        style={{ padding: 16, textAlign: "center" }}
      >
        {isUpdateOperation && updateData?.operationType === "delete"
          ? "Are you sure want to delete this workflow"
          : `${
              updateData?.workflowId
                ? `Update ${updateData?.instructionType || ""}`
                : "Add"
            } ${isOpenCreateWorkflowDialog?.instructionType || ""} Workflow`}
      </DialogTitle>
      <DialogContent>
        <TextField
          // autoFocus
          margin="dense"
          className="inputTextField border_color"
          label="Name"
          type="text"
          fullWidth
          required
          value={createWorkflowData?.name}
          variant="standard"
          onChange={(e) => handleChange("name", e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={isError && !createWorkflowData?.name}
          helperText={
            isError && !createWorkflowData?.name && VALIDATION_MSG?.REQUIRED
          }
          // disabled={isUpdateOperation && updateData?.operationType === "delete"}
        />
        <TextField
          margin="dense"
          className="inputTextField border_color"
          label="Description"
          type="text"
          fullWidth
          required
          value={createWorkflowData?.description}
          variant="standard"
          onChange={(e) => handleChange("description", e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={isError && !createWorkflowData?.description}
          helperText={
            isError &&
            !createWorkflowData?.description &&
            VALIDATION_MSG?.REQUIRED
          }
          // disabled={isUpdateOperation && updateData?.operationType === "delete"}
        />
      </DialogContent>
      <DialogActions
        style={{ display: "flex", justifyContent: "space-around" }}
      >
        <Button
          className="cancel_button"
          onClick={handleClose}
          disabled={isLoader}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          className="save_button"
          onClick={handleClickCreateWorkflow}
          disabled={isLoader}
        >
          {isLoader
            ? updateData?.workflowId
              ? "Updating..."
              : isUpdateOperation && updateData?.operationType === "delete"
              ? "Deleting"
              : "Creating..."
            : updateData?.workflowId &&
              (updateData?.operationType === "rename" ||
                updateData?.operationType === "duplicate")
            ? "Update"
            : isUpdateOperation && updateData?.operationType === "delete"
            ? "Delete"
            : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUpdateWorkflowDialog;
